import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { cn } from "@/lib/cn";
import { widgetLayoutFor, resolveCreatedSize } from "@/lib/frame-defaults";
import { localLink } from "@/lib/local-file-link";
import { isPdfFile, pdfSourceKey } from "@/lib/pdf-document";
import { useFrameSurface } from "../shared/use-frame-surface";
import { FileObject } from "./file-object";
import { FilePdfView } from "./file-pdf-view";
import { useFileActions } from "./use-file-actions";

/**
 * A file: its card, larger, with what it offers here (`useFileActions`). A
 * PDF in a frame of card size or more (focus, preview, a large canvas node)
 * shows the document itself, or its cover with why it cannot.
 */
export function FileFrame({ id, data, width, height }: FrameProps) {
  const frameSurface = useFrameSurface(id);
  const content = data.content ?? {};
  const name = typeof content.name === "string" ? content.name : "";
  const actions = useFileActions(data);
  const size = resolveCreatedSize("file", { content });
  const layout = widgetLayoutFor({ w: width ?? size.w, h: height ?? size.h });
  if (layout !== "card" || !isPdfFile(content)) {
    return <FileObject id={id} data={data} actions={actions} />;
  }
  // A located file, or this computer now known, reads the document afresh.
  const viewKey = [
    actions.pdf ? pdfSourceKey(actions.pdf) : "",
    localLink(content)?.path ?? "",
    actions.pending,
  ].join("\n");
  return (
    <div
      className={cn(
        frameSurface.className,
        "relative flex h-full flex-col overflow-hidden",
      )}
      data-testid="file-frame"
      {...frameSurface.attributes}
    >
      <FilePdfView key={viewKey} data={data} name={name} actions={actions} />
    </div>
  );
}
